import type { CustomDimensions, Step } from '../types/types';
import { PositionEngine } from '../types/enums';
import { normalizeCustomDimensions } from '@de/utils/normalizeCustomDimensions';
import { mergeCustomDimensions } from './configurable-step/applyProductContext';
import { ELEMENT_CUSTOM_DIMENSION_KEYS } from './configurable-step/resolveElementFields';
import { slugifyProductValue } from './configurable-step/resolveProductFields';
// Single source of truth for the spec-compliant `formName` used by every
// Form/Field event in the dynamic-experience flow (§6 / §8 / §9). Importing
// from the leaf module avoids pulling the events barrel and prevents an
// import cycle (`buildEventContext` only imports the `EventContext` *type*
// from this file, which TypeScript erases at runtime).
import { DEFAULT_FORM_NAME } from '../events/buildEventContext';
import { sanitizeOutboundUrlForTelemetry } from './sanitizeOutboundUrlForTelemetry';

/**
 * Canonical wire fields on `ProductClicked.product.*` that the DSL
 * `tracking.productFields` map can override. Module-scope so the
 * explicit slugified assignments in {@link createProductClickedPayload}
 * and the `omitCanonical` spread helper can't drift apart.
 */
const CANONICAL_PRODUCT_FIELDS = ['variant', 'category', 'name'] as const;

// Types for event parameters
export interface EventContext {
  dcs?: {
    degree: string | undefined;
    category: string | undefined;
    subject: string | undefined;
  };
  currentStep?: Step;
  currentStepType?: string | undefined;
  viewCorrelationId?: string;
  correlationId?: string;
  elementLoadedCorrelationId?: string;
  formViewedCorrelationId?: string;
  productViewedCorrelationId?: string;
  productClickedCorrelationId?: string;
  experienceVariant?: string;
  formatType?: string;
  formatSubtype?: string;
  presets?: {
    degree?: string;
    category?: string;
    subject?: string;
  };
  /**
   * DSL-resolved product override map. When set, populates the product
   * lifecycle payloads from the entry-point DSL `tracking.productFields`
   * block or product-surface tracking defaults:
   *   - Canonical fields (`variant`, `category`, `name`) take
   *     precedence over the legacy `dcs.{degree, subject, category}`
   *     reads in {@link createProductClickedPayload}.
   *   - Arbitrary additional keys (`brand`, future fields) spread
   *     onto the wire `product` object verbatim.
   *
   * Slugify is applied to canonical-field values for parity with the
   * legacy DCS path and affiliate `_meta.tracking.productName`
   * overrides (those still arrive unslugified). Resolver output is
   * already slugified; a second pass through
   * {@link slugifyProductValue} is idempotent.
   */
  product?: Record<string, string>;
  brand?: string;
  placement?: string;
  position?: number;
  sku?: string;
  productId?: string;
  listId?: string;
  /**
   * Custom dimensions surfaced to Tagular in the
   * canonical `{ key, value }` shape Tagular's ingestion endpoint expects.
   *
   * Common keys: `publisherId`, `extclid`, `utmSource`,
   * `utmMedium`, `utmCampaign`, `utmTerm`, `utmContent`, `refAnon`, `refInst`,
   * `refSess`, `refTenant`, `transitId`.
   *
   * Values are always serialized as strings before they reach the wire
   */
  customDimensions?: CustomDimensions;
  /**
   * Resolved `tracking.customDimensions` overlay. Payload builders
   * merge this additively onto platform dims after `dslConfigId`
   * append; the same key from config replaces the platform value.
   * Missing / empty leaves platform dims bit-identical.
   */
  mappedCustomDimensions?: CustomDimensions;
  fieldName?: string;
  fieldType?: string;
  fieldValue?: string;
  fieldNumber?: number;
  fieldLabel?: string;
  fieldId?: string;
  field?: {
    fieldName: string;
    fieldValue: string;
    fieldType?: string;
    fieldNumber?: number;
    fieldLabel?: string;
    autofilled?: boolean;
    fieldId?: string;
  }[];
  autofilled?: boolean;
  currentStepIndex?: number;
  monarch?: Record<string, unknown>;
  // §6.8: inline (no-redirect) CTAs emit `outboundUrl: null` so consumers
  // can distinguish "no outbound URL" from an empty string. Redirecting
  // CTAs continue to emit a string.
  outboundUrl?: string | null;
  actionOutcome?: string;
  experienceName?: string;
  slotName?: string;
  slotId?: string;
  slotVariant?: string;
  htmlId?: string;
  elementType?: string;
  name?: string;
  text?: string;
  location?: string;
  variant?: string;
  formId?: string;
  formName?: string;
  formType?: string;
  stepName?: string;
  stepId?: string;
  stepType?: string;
  stepNumber?: number;
  blockId?: string;
  /**
   * DSL result-step config id (`dslConfig.configId`). Set only on the
   * governed DSL result/lead path, where it adds a `{ key: 'dslConfigId' }`
   * `customDimensions` entry to the product events. Form and field events
   * (`FormViewed` / `FieldViewed` / `FieldInputted`) ignore it — their
   * registered schemas do not define a top-level `customDimensions` and the
   * Tagular endpoint rejects it with HTTP 400. Absent on the imperative
   * `ResultsPage`, leaving those payloads unchanged.
   */
  dslConfigId?: string;
  /**
   * Published config version. Read by configured-event composition.
   * Built-in Product payloads do not copy it onto the wire.
   */
  dslConfigVersion?: number;
  /**
   * Product wire keys from `tracking.productFields` that did not
   * resolve. Configured events name their own misses. Built-in
   * Product payloads do not copy this list onto the wire.
   */
  productFieldFallbacks?: readonly string[];
}

export type TagularEventName = string;

export interface TagularRequest {
  name: TagularEventName;
  payload: any;
}

// Form constants
const FLOW_TYPE = 'dexp-flow';
const FORM_VERSION = '1.0.0';

// Element type constants
const ELEMENT_TYPE_DYNAMIC_APP = 'dynamic-experiences-app';
const ELEMENT_NAME_DYNAMIC_APP = 'dynamic-experiences-v1';

// Location/placement defaults
const DEFAULT_PLACEMENT = 'personalizedQdf';

/**
 * Emit-time slugify for the canonical triad. Uses
 * {@link slugifyProductValue} so configured mapper output and DCS /
 * affiliate fallbacks share one algorithm. A second pass over
 * resolver output is idempotent — affiliate `product.name` still
 * needs this pass because Trackable does not slugify it first.
 *
 * @param {string} text - Raw triad value
 * @returns {string} Slugified value
 */
const slugify = (text: string): string => slugifyProductValue(text);

// removes the fieldNumber as this is no longer accepted in their schema
const removeFieldNumberFromField = (
  field: EventContext['field'],
): EventContext['field'] =>
  field?.map(({ fieldNumber: _fieldNumber, ...rest }) => ({ ...rest }));

export const createProductLoadedPayload = (context: EventContext) => {
  const {
    viewCorrelationId,
    formatType,
    formatSubtype,
    presets,
    dcs,
    product,
    placement,
    customDimensions,
    dslConfigId,
    mappedCustomDimensions,
    brand,
    location,
    position,
    sku,
    productId,
    listId,
  } = context;

  // Platform dims first (incl. `dslConfigId` append), then the
  // resolved `tracking.customDimensions` overlay. Config key wins.
  const productCustomDimensions = toProductCustomDimensions(
    customDimensions,
    dslConfigId,
    mappedCustomDimensions,
  );
  const variant = slugify(
    product?.variant ?? dcs?.degree ?? presets?.degree ?? '',
  );
  const category = slugify(
    product?.category ?? dcs?.category ?? presets?.category ?? '',
  );
  const name = slugify(product?.name ?? dcs?.subject ?? presets?.subject ?? '');
  const productExtras = product ? omitCanonical(product) : undefined;

  return {
    name: 'ProductLoaded',
    payload: {
      viewCorrelationId,
      ...(productCustomDimensions
        ? { customDimensions: productCustomDimensions }
        : {}),
      ...(listId ? { listId } : {}),
      product: {
        formatSubtype,
        formatType,
        variant,
        name,
        category,
        location: location || placement || '',
        positionEngine: PositionEngine.ALGOLIA,
        ...(brand ? { brand } : {}),
        ...(position !== undefined ? { position } : {}),
        ...(sku ? { sku } : {}),
        ...(productId ? { productId } : {}),
        ...(productExtras ?? {}),
      },
    },
  };
};

export const createElementLoadedPayload = (context: EventContext) => {
  const {
    currentStep,
    elementLoadedCorrelationId,
    placement,
    monarch,
    elementType,
    name,
    text,
    location,
    productClickedCorrelationId,
    htmlId,
  } = context;

  return {
    name: 'ElementLoaded',
    payload: {
      correlationId: productClickedCorrelationId || elementLoadedCorrelationId,
      webElement: {
        htmlId: htmlId || currentStep?.flowId || '',
        elementType: elementType || ELEMENT_TYPE_DYNAMIC_APP,
        name: name || ELEMENT_NAME_DYNAMIC_APP,
        location:
          location !== undefined ? location : placement || DEFAULT_PLACEMENT,
        text: text !== undefined ? text : JSON.stringify(monarch) || '',
      },
    },
  };
};

// Spec (§6 / §8 / §9): every Form/Field event for the dynamic-experience flow
// uses `formName: 'dexp-question-flow'`. Honor an explicit context override
// when set (the eventing abstraction supplies the spec-compliant value), then
// fall back to the canonical default exported by the events module.
const resolveFormName = (context: EventContext): string =>
  context.formName || DEFAULT_FORM_NAME;

/**
 * Append a `{ key: 'dslConfigId' }` entry onto an existing (already
 * normalized) `customDimensions` array, preserving entries the event
 * already carries (`sourceCorrelationId`, `flowId`, …). Used by
 * ProductLoaded and ProductViewed. Returns the input unchanged when
 * `dslConfigId` is absent. ProductClicked does not call this.
 */
const appendDslConfigIdDimension = (
  customDimensions: CustomDimensions | undefined,
  dslConfigId: string | undefined,
): CustomDimensions | undefined => {
  if (!dslConfigId) return customDimensions;
  return [
    ...(customDimensions ?? []),
    { key: 'dslConfigId', value: dslConfigId },
  ];
};

/**
 * Platform dims first (including optional `dslConfigId` append), then
 * the resolved config overlay. Same key from config replaces the
 * platform value. No overlay leaves platform dims unchanged.
 */
const toProductCustomDimensions = (
  customDimensions: CustomDimensions | undefined,
  dslConfigId: string | undefined,
  mappedCustomDimensions: CustomDimensions | undefined,
): CustomDimensions | undefined => {
  const platform = appendDslConfigIdDimension(
    normalizeCustomDimensions(customDimensions),
    dslConfigId,
  );
  return mergeCustomDimensions(
    platform,
    normalizeCustomDimensions(mappedCustomDimensions),
  );
};

export const createFormViewedPayload = (
  context: EventContext,
  isForConversionDrawer?: boolean,
) => {
  const {
    currentStep,
    formViewedCorrelationId,
    elementLoadedCorrelationId,
    currentStepIndex,
    productClickedCorrelationId,
    formName,
    stepName,
  } = context;

  if (isForConversionDrawer) {
    return {
      name: 'FormViewed',
      payload: {
        formContext: {
          formId: `dexp-${currentStep?.flowId}`,
          formName: formName || DEFAULT_FORM_NAME,
        },
        stepContext: {
          stepId: currentStep?.id || '',
          stepName: stepName || currentStep?.title || '',
          stepNumber: currentStepIndex,
        },
        correlationId: productClickedCorrelationId || formViewedCorrelationId,
      },
    };
  } else {
    return {
      name: 'FormViewed',
      payload: {
        sourceCorrelationId: elementLoadedCorrelationId,
        formContext: {
          formId: `dexp-${currentStep?.flowId}`,
          formType: FLOW_TYPE,
        },
        stepContext: {
          stepId: currentStep?.id || '',
          stepName: currentStep?.title || '',
          stepNumber: currentStepIndex,
        },
        correlationId: formViewedCorrelationId,
      },
    };
  }
};

export const createFormStartedPayload = (context: EventContext) => {
  const { formViewedCorrelationId, currentStep, currentStepIndex } = context;
  return {
    name: 'FormStarted',
    payload: {
      formContext: {
        formId: `dexp-${currentStep?.flowId}`,
        formName: resolveFormName(context),
        formType: FLOW_TYPE,
        formVersion: FORM_VERSION,
      },
      stepContext: {
        stepId: currentStep?.id || '',
        stepName: currentStep?.title || '',
        stepNumber: currentStepIndex,
      },
      correlationId: formViewedCorrelationId,
    },
  };
};

export const createFormContinuedPayload = (context: EventContext) => {
  const { formViewedCorrelationId, currentStep, currentStepIndex, field, dcs } =
    context;

  const sanitizedField = removeFieldNumberFromField(field);

  return {
    name: 'FormContinued',
    payload: {
      formContext: {
        formId: `dexp-${currentStep?.flowId}`,
        formName: resolveFormName(context),
        formType: FLOW_TYPE,
        formVersion: FORM_VERSION,
      },
      stepContext: {
        stepId: currentStep?.id || '',
        stepName: currentStep?.title || '',
        stepNumber: currentStepIndex,
      },
      correlationId: formViewedCorrelationId,
      field: sanitizedField ? sanitizedField : dcs,
    },
  };
};

export const createFormEventsPayload = (
  context: EventContext,
  eventName: TagularEventName,
) => {
  const {
    formViewedCorrelationId,
    productClickedCorrelationId,
    currentStep,
    currentStepIndex,
    field,
    dcs,
    stepName,
    stepNumber,
    outboundUrl,
  } = context;
  const sanitizedField = removeFieldNumberFromField(field);
  const sanitizedOutboundUrl = sanitizeOutboundUrlForTelemetry(outboundUrl);

  const eventPayload: TagularRequest = {
    name: eventName,
    payload: {
      formContext: {
        formId: `dexp-${currentStep?.flowId}`,
        formName: resolveFormName(context),
        formType: FLOW_TYPE,
        formVersion: FORM_VERSION,
      },
      stepContext: {
        stepId: currentStep?.id || '',
        stepName: stepName || currentStep?.title || '',
        stepNumber: stepNumber || currentStepIndex,
      },
      field: sanitizedField
        ? sanitizedField
        : [
            { fieldName: 'degree', fieldValue: dcs?.degree || '' },
            { fieldName: 'category', fieldValue: dcs?.category || '' },
            { fieldName: 'subject', fieldValue: dcs?.subject || '' },
          ],
      correlationId: productClickedCorrelationId || formViewedCorrelationId,
      ...(eventName === 'FormSubmitted' && sanitizedOutboundUrl
        ? { outboundUrl: sanitizedOutboundUrl }
        : {}),
    },
  };

  return eventPayload;
};

export const createProductViewedPayload = (context: EventContext) => {
  const {
    viewCorrelationId,
    formatSubtype,
    formatType,
    placement,
    presets,
    dcs,
    product,
    customDimensions,
    dslConfigId,
    mappedCustomDimensions,
    brand,
    location,
    position,
    sku,
    productId,
    listId,
  } = context;

  // Mirror `createProductLoadedPayload` so `ProductViewed` stays at
  // parity. The popup recommender surfaces set a top-level
  // `dslConfigId`; the `Trackable` DSL-card path instead bakes it into
  // `customDimensions` upstream and leaves `context.dslConfigId` unset
  // (so this is a no-op there, avoiding a duplicate).
  const productCustomDimensions = toProductCustomDimensions(
    customDimensions,
    dslConfigId,
    mappedCustomDimensions,
  );
  // Mirror `createProductLoadedPayload`/`createProductClickedPayload`:
  // prefer the DSL-resolved `product` override, fall back to the per-card
  // `dcs`, then page-level `presets`. `??` (not `||`) is intentional —
  // `resolveProductFields` strips empty strings before they reach
  // `EventContext.product`, so an empty `product.variant` means
  // "intentionally absent" and must not be overridden by `dcs`/`presets`.
  const variant = slugify(
    product?.variant ?? dcs?.degree ?? presets?.degree ?? '',
  );
  const category = slugify(
    product?.category ?? dcs?.category ?? presets?.category ?? '',
  );
  const name = slugify(product?.name ?? dcs?.subject ?? presets?.subject ?? '');
  const productExtras = product ? omitCanonical(product) : undefined;

  return {
    name: 'ProductViewed',
    payload: {
      viewCorrelationId,
      ...(listId ? { listId } : {}),
      product: {
        formatSubtype,
        formatType,
        location: placement || '',
        name,
        variant,
        category,
        positionEngine: PositionEngine.ALGOLIA,
        ...(brand ? { brand } : {}),
        ...(location ? { location } : {}),
        ...(position !== undefined ? { position } : {}),
        ...(sku ? { sku } : {}),
        ...(productId ? { productId } : {}),
        ...(productExtras ?? {}),
      },
      ...(productCustomDimensions
        ? { customDimensions: productCustomDimensions }
        : {}),
    },
  };
};

export const createProductClickedPayload = (context: EventContext) => {
  const {
    formViewedCorrelationId,
    outboundUrl,
    actionOutcome,
    formatSubtype,
    formatType,
    presets,
    dcs,
    product,
    viewCorrelationId,
    customDimensions,
    mappedCustomDimensions,
    productClickedCorrelationId,
    // Host-supplied correlation (registered-defaults dashboards). Only
    // `createElementClickedPayload` read this; a configured ProductClicked
    // composed on a dashboard host had no flow correlation to fall back on
    // and this was the one source left unread, so its correlationId was
    // silently dropped instead of carrying the host's own value.
    correlationId,
    brand,
    location,
    placement,
    position,
    sku,
    productId,
    listId,
  } = context;

  // Clicked does not append `dslConfigId` (Loaded/Viewed do). Overlay
  // configured dims onto the normalized platform set only. Configured
  // events add their own stamps after this builder returns.
  const productCustomDimensions = toProductCustomDimensions(
    customDimensions,
    undefined,
    mappedCustomDimensions,
  );

  // Prefer the DSL-resolved `product` override for canonical fields
  // (`variant`, `category`, `name`); fall back to `dcs`/`presets`.
  //
  // `??` (not `||`) is intentional: `resolveProductFields` filters out
  // empty strings before they reach `EventContext.product`, so an empty
  // `product.variant` means "intentionally absent" and must NOT be
  // silently overridden by `presets.degree`.
  const variant = slugify(
    product?.variant ?? dcs?.degree ?? presets?.degree ?? '',
  );
  const category = slugify(
    product?.category ?? dcs?.category ?? presets?.category ?? '',
  );
  const name = slugify(product?.name ?? dcs?.subject ?? presets?.subject ?? '');

  // Strip canonical fields from the spread so the slugified values
  // above stay authoritative; everything else passes through verbatim.
  const productExtras = product ? omitCanonical(product) : undefined;

  return {
    name: 'ProductClicked',
    payload: {
      // Flow correlation wins when present; the host value is a fallback
      // for hosts (registered-defaults) that never set either flow id.
      correlationId:
        productClickedCorrelationId || formViewedCorrelationId || correlationId,
      viewCorrelationId,
      outboundUrl: sanitizeOutboundUrlForTelemetry(outboundUrl),
      actionOutcome,
      ...(productCustomDimensions
        ? { customDimensions: productCustomDimensions }
        : {}),
      ...(listId ? { listId } : {}),
      product: {
        formatSubtype,
        formatType,
        name,
        category,
        variant,
        positionEngine: PositionEngine.ALGOLIA,
        // Mirror `createProductLoaded/ViewedPayload`: fall back to the
        // Monarch slot placement when no explicit `location` is set, so EL
        // cards (no registry/identity `location`) report the slot
        // (`aboveTheFold`) rather than dropping `location` entirely. Result
        // cards still report `dexp-results-page` via their registry default.
        location: location || placement || '',
        ...(brand ? { brand } : {}),
        ...(position !== undefined ? { position } : {}),
        ...(sku ? { sku } : {}),
        ...(productId ? { productId } : {}),
        // Arbitrary author-declared product fields. Spread last so
        // explicit DSL fields win over the static `EventContext.brand`.
        ...(productExtras ?? {}),
      },
    },
  };
};

/**
 * Returns a copy of the resolved `product` map without the canonical
 * fields in {@link CANONICAL_PRODUCT_FIELDS} so the spread in
 * {@link createProductClickedPayload} does not double-write them.
 */
function omitCanonical(
  product: Record<string, string>,
): Record<string, string> {
  const canonical = new Set<string>(CANONICAL_PRODUCT_FIELDS);
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(product)) {
    if (canonical.has(key)) continue;
    out[key] = value;
  }
  return out;
}

export const createStepElementLoadedPayload = (context: EventContext) => {
  const {
    formViewedCorrelationId,
    currentStep,
    placement,
    productClickedCorrelationId,
  } = context;
  return {
    name: 'ElementLoaded',
    payload: {
      correlationId: productClickedCorrelationId || formViewedCorrelationId,
      webElement: {
        htmlId: currentStep?.id,
        elementType: `dexp-${currentStep?.flowId}-${currentStep?.id?.split('-')[0]}`,
        name: currentStep?.title,
        location: placement || '',
        text: '',
      },
    },
  };
};

export const createStepElementViewedPayload = (context: EventContext) => {
  const {
    productClickedCorrelationId,
    formViewedCorrelationId,
    currentStep,
    elementType,
    name,
    location,
    text,
    htmlId,
    variant,
    experienceVariant,
    customDimensions,
  } = context;
  const normalizedCustomDimensions =
    normalizeCustomDimensions(customDimensions);
  return {
    name: 'ElementViewed',
    payload: {
      correlationId: productClickedCorrelationId || formViewedCorrelationId,
      webElement: {
        htmlId: htmlId || currentStep?.id,
        elementType:
          elementType ||
          `dexp-${currentStep?.flowId}-${currentStep?.id?.split('-')[0]}`,
        name: name || currentStep?.title,
        location: location || '',
        text: text || '',
        variant: variant || experienceVariant,
      },
      ...(normalizedCustomDimensions
        ? { customDimensions: normalizedCustomDimensions }
        : {}),
    },
  };
};

/**
 * Build a `FieldViewed` / `FieldInputted` / `FieldSelected` payload.
 *
 * Field events deliberately never place a top-level `customDimensions`
 * array on the wire: the registered `core.fieldViewed.v1` /
 * `redventures.usertracking.v3.FieldInputted` / `FieldSelected` schemas
 * do not define that property, and sending it makes the Tagular
 * ingestion endpoint reject the request with HTTP 400. `EventContext`
 * still accepts `customDimensions` / `dslConfigId` so call sites stay
 * uniform with the product/form builders, but neither is emitted here.
 */
export const createFieldEventsPayload = (
  context: EventContext,
  eventName: TagularEventName,
) => {
  const {
    formViewedCorrelationId,
    currentStep,
    fieldName,
    fieldType,
    fieldValue,
    fieldNumber,
    fieldLabel,
    fieldId,
    autofilled,
    currentStepIndex,
    stepName,
    stepId,
    productClickedCorrelationId,
  } = context;

  const resolvedFormName = resolveFormName(context);
  const resolvedStepName = stepName || currentStep?.title || '';
  const resolvedStepId = stepId ?? currentStep?.id ?? '';
  const resolvedStepNumber = currentStepIndex ?? undefined;

  return {
    name: eventName,
    payload: {
      correlationId: productClickedCorrelationId || formViewedCorrelationId,
      formContext: {
        formId: `dexp-${currentStep?.flowId}`,
        formName: resolvedFormName,
        formType: FLOW_TYPE,
        formVersion: FORM_VERSION,
      },
      userInputField: {
        fieldName:
          fieldName ??
          (currentStep?.leadFieldAssociated as string | undefined) ??
          currentStep?.stepName ??
          currentStep?.title ??
          currentStep?.id ??
          '',
        fieldType: fieldType ?? '',
        fieldValue: fieldValue ?? '',
        fieldLabel: fieldLabel ?? '',
        fieldId: fieldId ?? '',
        ...(autofilled ? { autofilled } : {}),
        ...(fieldNumber ? { fieldNumber } : {}),
      },
      stepContext: {
        stepId: resolvedStepId,
        stepName: resolvedStepName,
        stepNumber: resolvedStepNumber,
      },
    },
  };
};

export const createModalPayload = (
  eventName: TagularEventName,
  context: EventContext,
) => {
  const {
    formViewedCorrelationId,
    currentStep,
    viewCorrelationId,
    customDimensions,
  } = context;
  const normalizedCustomDimensions =
    normalizeCustomDimensions(customDimensions);

  return {
    name: eventName,
    payload: {
      correlationId: formViewedCorrelationId,
      webElement: {
        htmlId: currentStep?.id,
        elementType: 'dexp-pop-up',
        name: 'dexp-terms-and-conditions',
        text: currentStep?.modal?.buttonText || 'Agree & See Matches',
      },
      viewCorrelationId,
      ...(normalizedCustomDimensions
        ? { customDimensions: normalizedCustomDimensions }
        : {}),
    },
  };
};

export const createModalClosedPayload = (
  eventName: TagularEventName,
  context: EventContext,
) => {
  const {
    formViewedCorrelationId,
    currentStep,
    viewCorrelationId,
    customDimensions,
  } = context;
  const normalizedCustomDimensions =
    normalizeCustomDimensions(customDimensions);

  return {
    name: eventName,
    payload: {
      correlationId: formViewedCorrelationId,
      webElement: {
        htmlId: currentStep?.id,
        elementType: 'dexp-pop-up',
        name: 'dexp-terms-and-conditions-exit',
        text: '',
      },
      viewCorrelationId,
      ...(normalizedCustomDimensions
        ? { customDimensions: normalizedCustomDimensions }
        : {}),
    },
  };
};

export const createElementClickedPayload = (context: EventContext) => {
  const {
    productClickedCorrelationId,
    formViewedCorrelationId,
    elementType,
    name,
    location,
    htmlId,
    text,
    correlationId,
    viewCorrelationId,
    position,
    customDimensions,
  } = context;
  const normalizedCustomDimensions =
    normalizeCustomDimensions(customDimensions);

  return {
    name: 'ElementClicked',
    payload: {
      correlationId:
        correlationId || productClickedCorrelationId || formViewedCorrelationId,
      viewCorrelationId: viewCorrelationId,
      webElement: {
        elementType: elementType || 'dexp-hosted-lead-application-cta',
        name: name || 'dexp-cta-button',
        location: location || '',
        ...(position !== undefined ? { position: String(position) } : {}),
        ...(htmlId ? { htmlId } : {}),
        ...(text ? { text } : {}),
      },
      ...(normalizedCustomDimensions
        ? { customDimensions: normalizedCustomDimensions }
        : {}),
    },
  };
};

export const HOST_ELEMENT_CONTEXT_KEYS = [
  'correlationId',
  'viewCorrelationId',
  'elementType',
  'name',
  'htmlId',
  'location',
  'text',
] as const;

type HostElementContextKey = (typeof HOST_ELEMENT_CONTEXT_KEYS)[number];

export type HostElementEventContext = Pick<EventContext, HostElementContextKey>;

export const createHostElementEventContext = (
  context: Readonly<Record<string, unknown>>,
): HostElementEventContext => {
  const hostContext: Partial<Record<HostElementContextKey, string>> = {};

  for (const key of HOST_ELEMENT_CONTEXT_KEYS) {
    const value = context[key];
    if (typeof value === 'string') {
      hostContext[key] = value;
    }
  }

  return hostContext;
};

const HOST_CUSTOM_DIMENSION_KEY_SET: ReadonlySet<string> = new Set(
  ELEMENT_CUSTOM_DIMENSION_KEYS,
);

const sanitizeHostCustomDimensions = (
  value: unknown,
): CustomDimensions | undefined => {
  const normalized = normalizeCustomDimensions(
    value as CustomDimensions | undefined,
  );
  if (!normalized || normalized.length === 0) {
    return undefined;
  }
  const allowlisted = normalized.filter((entry) =>
    HOST_CUSTOM_DIMENSION_KEY_SET.has(entry.key),
  );
  return allowlisted.length > 0 ? allowlisted : undefined;
};

const createHostElementWirePayload = (
  eventName: 'ElementLoaded' | 'ElementViewed' | 'ElementClicked',
  context: Readonly<Record<string, unknown>>,
) => {
  const sanitized = createHostElementEventContext(context);
  // core.ElementLoaded.v1 rejects any customDimensions property.
  const customDimensions =
    eventName === 'ElementLoaded'
      ? undefined
      : sanitizeHostCustomDimensions(context.customDimensions);
  return {
    name: eventName,
    payload: {
      correlationId: sanitized.correlationId,
      ...(eventName !== 'ElementLoaded'
        ? { viewCorrelationId: sanitized.viewCorrelationId }
        : {}),
      webElement: {
        elementType: sanitized.elementType ?? '',
        name: sanitized.name ?? '',
        location: sanitized.location ?? '',
        text: sanitized.text ?? '',
        ...(sanitized.htmlId ? { htmlId: sanitized.htmlId } : {}),
      },
      ...(customDimensions ? { customDimensions } : {}),
    },
  };
};

export const createHostElementClickedPayload = (
  context: Readonly<Record<string, unknown>>,
) => createHostElementWirePayload('ElementClicked', context);

export const createHostElementViewedPayload = (
  context: Readonly<Record<string, unknown>>,
) => createHostElementWirePayload('ElementViewed', context);

export const createHostElementLoadedPayload = (
  context: Readonly<Record<string, unknown>>,
) => createHostElementWirePayload('ElementLoaded', context);

export const createHostFieldEventsPayload = (
  context: EventContext,
  eventName: TagularEventName,
) => {
  const userInputField = {
    fieldName: context.fieldName ?? '',
    fieldType: context.fieldType ?? '',
    fieldLabel: context.fieldLabel ?? '',
    fieldId: context.fieldId ?? '',
    fieldValue: context.fieldValue ?? '',
  };
  return {
    name: eventName,
    payload: {
      correlationId: context.correlationId,
      formContext: {
        formId: context.formId ?? '',
        formName: context.formName ?? '',
        formType: context.formType ?? '',
        formVersion: FORM_VERSION,
      },
      userInputField,
      stepContext: {
        stepId: context.stepId ?? '',
        stepName: context.stepName ?? '',
      },
    },
  };
};

export const createElementViewedPayload = (context: EventContext) => {
  const {
    correlationId,
    elementType,
    name,
    location,
    text,
    htmlId,
    viewCorrelationId,
    position,
    customDimensions,
  } = context;
  const normalizedCustomDimensions =
    normalizeCustomDimensions(customDimensions);
  return {
    name: 'ElementViewed',
    payload: {
      correlationId,
      viewCorrelationId,
      webElement: {
        elementType: elementType || '',
        name: name || '',
        location: location || '',
        text: text || '',
        ...(htmlId ? { htmlId } : {}),
        ...(position !== undefined ? { position: String(position) } : {}),
      },
      ...(normalizedCustomDimensions
        ? { customDimensions: normalizedCustomDimensions }
        : {}),
    },
  };
};
